import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
	endOfDay,
	startOfDay,
	startOfMonth,
	startOfYear,
	subDays,
	subMonths,
} from 'date-fns';

import { loginState } from 'global-stores';
import { NumberWithCommas } from 'utils';

import { StatusChip, StepChain, useSyncedPulse } from 'views/design-system';
import { SessionTreeGraph } from 'views/complex-onboarding-flow/components';
/* The Applicant cell is the Sessions list's "Onboarding Details" cell — same
   markup, same helpers, same stylesheet. Importing the helpers rather than
   restating them is what keeps the two from drifting the next time either is
   touched. */
import {
	fundLabel,
	hasOfacWarning,
	stamp,
} from 'views/sessions/deck/derive';
import { ComplexStepsView } from 'views/pipelines/components/complex-step-view';
// the diagram's styling ships with the modal, which is code-split — without this
// the nodes render unstyled and 0px wide, and react-flow will not draw an edge
// between zero-width nodes
import 'views/pipelines/components/complex-step-view/complex-step-view.scss';
import {
	ageTone,
	toMetricTiles,
	toReviewQueue,
	toSessionRows,
	toWatchFlows,
} from './derive';
import { REVIEW_TARGET_MINUTES } from './constants';
import { useOpsDeck } from './use-ops-deck';
import { useOpsTheme } from './use-ops-theme';
import type { IStatusSplit } from './types';
import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './ops-deck.scss';

const today = () => endOfDay(new Date());

/**
 * `isDefault` marks the one range routes-children already primed into
 * DashboardsAtom at app mount (last 30 days). Selecting it costs no
 * /onboarding-summary request; every other preset re-fetches.
 */
const PRESETS: { label: string; start: () => Date; isDefault?: boolean }[] = [
	{ label: 'Today', start: () => startOfDay(new Date()) },
	{ label: 'Last 7 days', start: () => subDays(new Date(), 7) },
	{ label: 'Last 30 days', start: () => subDays(new Date(), 30), isDefault: true },
	{ label: 'This month', start: () => startOfMonth(new Date()) },
	{ label: 'Last quarter', start: () => subMonths(new Date(), 3) },
	{ label: 'This year', start: () => startOfYear(new Date()) },
];

const DEFAULT_PRESET = Math.max(0, PRESETS.findIndex(p => p.isDefault));

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const presetAt = (i: number) => PRESETS[i] ?? PRESETS[DEFAULT_PRESET] ?? PRESETS[0]!;

const dayLabel = (d: Date) =>
	d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/** "612 approved · 188 in review" — only the parts that are non-zero. */
const splitNote = (split: IStatusSplit) => {
	const parts: string[] = [];
	if (split.completed) parts.push(`${split.completed} approved`);
	if (split.processing) parts.push(`${split.processing} in review`);
	if (split.pending) parts.push(`${split.pending} pending`);
	if (split.rejected + split.failed)
		parts.push(`${split.rejected + split.failed} rejected`);
	return parts.length ? parts.join(' · ') : 'no activity in range';
};

/**
 * The completion tick, placed against the RIGHT EDGE OF THE LAST NODE.
 *
 * It cannot be a plain sibling of the diagram: that container is a full-width
 * block, so the tick landed underneath the chain rather than beside it. And it
 * cannot simply be floated right either — the diagram is only as wide as its
 * step count, so "the right of the column" is nowhere near "the right of the
 * last node" on a two-step flow.
 *
 * So it measures. react-flow lays the nodes out asynchronously and the labels
 * are the node widths, so the position is not knowable at first paint: the
 * measurement is retried on the next frame, again after the fonts settle, and
 * on any resize of the lane. Until it has a number the tick stays hidden rather
 * than flashing at the wrong x.
 */
/** The tick's own width, so it can be centred on a node without measuring itself. */
const TICK = 13;

const FlowDoneTick: FC<{ title: string }> = ({ title }) => {
	const ref = useRef<HTMLSpanElement | null>(null);
	const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

	useEffect(() => {
		const host = ref.current?.parentElement;
		if (!host) return undefined;
		const lane = host.querySelector('.rowflow');
		if (!lane) return undefined;

		const place = () => {
			/* THE LAST SUCCESSFUL NODE, not simply the rightmost one.
			   The tick marks a flow that FINISHED, so it belongs under the last
			   step that actually completed — on a chain whose tail is still
			   pending or under review, the rightmost node is not the one being
			   reported on. `data-flow-state` is published by the node renderer
			   (react-flow-graph-chart.tsx) precisely so styling and positioning
			   can read the state without re-deriving it from a fill colour. */
			const done = lane.querySelectorAll('[data-flow-state="completed"]');
			/* NOTHING HAS SUCCEEDED YET — nothing to mark. Cleared rather than
			   left at its last position, so a session whose steps are all pending
			   or under review shows no tick at all instead of one pointing at a
			   node that never completed. */
			if (!done.length) {
				setPos(null);
				return;
			}
			/* NodeList indexing is typed as possibly-undefined even after the
			   length check above, so narrow it explicitly rather than asserting. */
			const target = done.item(done.length - 1);
			if (!target) {
				setPos(null);
				return;
			}

			const host = ref.current?.parentElement;
			if (!host) return;
			const h = host.getBoundingClientRect();
			const t = target.getBoundingClientRect();
			if (!t.width) return;

			/* Centred on that node and sitting just under it. Measured against the
			   wrapper rather than the lane: the lane clips (overflow:hidden, 66px)
			   and the tick deliberately sits below that line. */
			setPos({
				left: Math.round(t.left + t.width / 2 - h.left - TICK / 2),
				top: Math.round(t.bottom - h.top + 2),
			});
		};

		place();
		const raf = requestAnimationFrame(place);
		const t1 = setTimeout(place, 260);
		const t2 = setTimeout(place, 700);

		/* WATCH THE VIEWPORT TRANSFORM, not just the lane's size.
		   react-flow moves the nodes by transforming `.react-flow__viewport`, and
		   fitView does that AFTER first paint — the lane's own box never changes,
		   so a ResizeObserver on it never fired and the tick kept the position it
		   measured before the chain was placed. That is why it sat ~200px past
		   the last node instead of 8px: the number was right for where the nodes
		   used to be. */
		const vp = lane.querySelector('.react-flow__viewport');
		const mo = vp
			? new MutationObserver(place)
			: null;
		if (vp && mo) mo.observe(vp, { attributes: true, attributeFilter: ['style'] });

		const ro = new ResizeObserver(place);
		ro.observe(lane);

		return () => {
			cancelAnimationFrame(raf);
			clearTimeout(t1);
			clearTimeout(t2);
			mo?.disconnect();
			ro.disconnect();
		};
	}, []);

	return (
		<span
			ref={ref}
			className="donetick"
			title={title}
			aria-label="Last completed step"
			style={pos === null ? { visibility: 'hidden' } : pos}
		>
			{/* INLINE SVG, NOT AN ICON-FONT GLYPH. remixicon loads from cdnjs, and
			    external font requests are blocked here — Google Fonts fails with
			    ERR_BLOCKED_BY_ORB on every load. An <i class="ri-…"> then renders
			    as an empty box with no width: invisible, and indistinguishable
			    from the badge not being there at all. */}
			<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
				<circle cx="8" cy="8" r="7" fill="currentColor" />
				<path
					d="M4.8 8.3l2.1 2.1 4.3-4.3"
					fill="none"
					stroke="var(--neon-panel, #0d1117)"
					strokeWidth="1.8"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</span>
	);
};

export const OpsDeck = () => {
	const { toggle, isDark } = useOpsTheme();
	const login = useRecoilValue(loginState) ?? {};

	const [flowView, setFlowView] = useState<any>(null);
	const [presetIndex, setPresetIndex] = useState(DEFAULT_PRESET);
	const range = useMemo(() => {
		const preset = presetAt(presetIndex);
		return {
			start: preset.start(),
			end: today(),
			isDefault: !!preset.isDefault,
		};
	}, [presetIndex]);

	const { summary, chart, analytics, sessions, envelopes, loaded, error, reload } =
		useOpsDeck(range);

	const tiles = useMemo(
		() => toMetricTiles(analytics, chart?.data ?? []),
		[analytics, chart]
	);
	const queue = useMemo(() => toReviewQueue(analytics), [analytics]);
	const rows = useMemo(() => toSessionRows(sessions), [sessions]);
	const watch = useMemo(() => toWatchFlows(sessions), [sessions]);

	/* Every sv2pulse on this page beats together — see useSyncedPulse. */
	useSyncedPulse();

	/** Shared with the table cell below, so the strip and the rows agree. */
	const fmtWait = (m: number) => `${Math.floor(m / 60)}h ${m % 60}m`;

	const sessionTotals = useMemo(() => {
		const find = (id: string) =>
			(summary?.session ?? []).find((s: any) => s.id === id)?.value ?? 0;
		return {
			total: find('total'),
			completed: find('completed'),
			processing: find('processing'),
			pending: find('pending'),
			failed: find('failed'),
			flows:
				(summary?.onboarding ?? []).find((s: any) => s.id === 'total')?.value ?? 0,
		};
	}, [summary]);

	/**
	 * The meter's segments and the four labelled cells.
	 *
	 * `total` is read SEPARATELY from the four parts and can exceed their sum:
	 * `rejected` exists in the payload and is folded into the tile captions
	 * elsewhere, but sessionTotals never picks it up. Today the four happen to sum
	 * exactly, so it is invisible — any proportional bar exposes it the first time
	 * something is rejected. The remainder gets its own neutral segment rather
	 * than being silently absorbed into one of the others.
	 */
	const parts = useMemo(() => {
		const {total, completed, processing, pending, failed} = sessionTotals;
		const counted = completed + processing + pending + failed;
		const other = Math.max(0, total - counted);
		const pct = total ? Math.round((completed / total) * 100) : 0;
		return {
			// a zero segment must not render at all, or it takes a hairline of width
			segments: [
				{key: 'ok', value: completed},
				{key: 'rev', value: processing},
				{key: 'pend', value: pending},
				{key: 'rej', value: failed},
				{key: 'other', value: other},
			].filter(seg => seg.value > 0),
			legs: [
				{key: 'ok', value: completed, label: 'approved', note: `${pct}%`, on: true},
				{key: 'rev', value: processing, label: 'under review', note: '', on: processing > 0},
				/* "incomplete", matching the sessions tab of the same population:
				   these are sessions the APPLICANT has not finished, not a queue
				   anyone here is waiting on. The `pend` key and its token are
				   unchanged; only the word is. */
				{key: 'pend', value: pending, label: 'incomplete', note: '', on: pending > 0},
				/* "rejected", not "failed". These sessions were REJECTED — by the
				   engine or by a compliance agent — and every other surface calls
				   that state rejected: the status pill on the row, the Rejected
				   tab, the node colour. "failed" read as a technical fault
				   (something broke) rather than a decision that was taken. The
				   `rej` key and its token are unchanged; only the word is. */
				{key: 'rej', value: failed, label: 'rejected', note: '', on: failed > 0},
				...(other > 0
					? [{key: 'other', value: other, label: 'other', note: '', on: true}]
					: []),
			],
		};
	}, [sessionTotals]);

	const email = useMemo(() => {
		const find = (id: string) =>
			(summary?.email ?? []).find((s: any) => s.id === id)?.value ?? 0;
		return { total: find('total'), opened: find('opened'), bounced: find('bounced') };
	}, [summary]);

	const esign = useMemo(() => {
		const by = (...s: string[]) =>
			envelopes.filter((e: any) => s.includes(e?.status)).length;
		return {
			awaiting: by('configured', 'sent'),
			review: by('underReview'),
			completed: by('completed'),
			drafts: by('draft'),
			voided: by('voided'),
		};
	}, [envelopes]);

	const oldestWaiting = useMemo(
		() =>
			rows
				.filter(r => r.verdict === 'rev')
				.reduce((max, r) => Math.max(max, r.ageMinutes), 0),
		[rows]
	);

	const handlePreset = useCallback((i: number) => setPresetIndex(i), []);

	const n = (v: number) => NumberWithCommas(v, false);

	// `sv2` brings the shared surface primitives — including the reset for the
	// app's global letter-spacing, which otherwise widens every chip and cell
	// on this page the same way it did on the others.
	return (
		<div className="ops-scope sv2 ops-deck">
			<div className="hrow">
				<div className="hl">
					<h1>Operations — {presetAt(presetIndex).label.toLowerCase()}</h1>
					<div className="sub">
						{login?.businessName ? `${login.businessName} · ` : ''}
						{dayLabel(new Date())} ·{' '}
						{loaded ? `${n(sessionTotals.flows)} onboarding flows` : 'loading…'} ·{' '}
						{n(rows.length)} sessions in the live queue ·{' '}
						<button type="button" className="linky" onClick={reload}>
							refresh
						</button>
					</div>
				</div>
				<div className="theme-toggle">
					<span className={`tl ${!isDark ? 'on' : ''}`}>Light</span>
					<button
						className="tswitch"
						type="button"
						onClick={toggle}
						aria-label="Switch between light and dark theme"
						aria-pressed={isDark}
					>
						<span className="dots">
							<i />
							<i />
						</span>
						<span className={`knob ${isDark ? 'knob--dark' : ''}`} />
					</button>
					<span className={`tl ${isDark ? 'on' : ''}`}>Dark</span>
				</div>
			</div>

			<div className="ctl">
				<span className="range">
					{dayLabel(range.start)} – {dayLabel(range.end)}
				</span>
				{PRESETS.map((p, i) => (
					<button
						key={p.label}
						type="button"
						className={`pre ${i === presetIndex ? 'on' : ''}`}
						onClick={() => handlePreset(i)}
					>
						{p.label}
					</button>
				))}
				<span className="rn">
					{error ? 'summary unavailable' : loaded ? 'live' : 'fetching…'}
				</span>
			</div>

			<div className="strip">
				{tiles.map(t => (
					<div className="tile" key={t.key}>
						<div className="l">
							<span className={`mk mk${t.tone}`} />
							{t.label}
						</div>
						<div className="n">{loaded ? n(t.total) : '—'}</div>
						<div
							className={`d ${
								t.delta === null ? '' : t.delta >= 0 ? 'up' : 'dn'
							}`}
						>
							{!loaded
								? ' '
								: t.delta !== null
								? `${t.delta >= 0 ? '▲' : '▼'} ${Math.abs(t.delta)}%`
								: /* No daily series to trend against — say what the total is made
								     of instead of leaving the line empty. */
								  splitNote(t.split)}
						</div>
						{/* The chart, or WHY THERE ISN'T ONE.
						    A flat series is still not drawn — a dead-flat line across a
						    tile reads as a real and alarming trend rather than as "no
						    data". But leaving the space empty was its own problem: five
						    of these six tiles have no daily variation on real data, so
						    they rendered a big total with nothing under it and looked
						    like six charts of which one worked. Saying which it is costs
						    a line and removes the ambiguity. */}
						{t.points ? (
							<svg viewBox="0 0 100 26" preserveAspectRatio="none" aria-hidden="true">
								<polyline className={`sp${t.tone}`} strokeWidth="2" points={t.points} />
							</svg>
						) : (
							loaded && !!t.seriesNote && <div className="nospark">{t.seriesNote}</div>
						)}
					</div>
				))}
			</div>

			{/* NOT `.banner` — views/user-verification/banner/banner.scss declares a
			    GLOBAL, unscoped `.banner` (justify-content:center; width:100%) that is
			    imported app-wide. `.ops-deck .banner` beat it on every property it
			    declared but never declared those two, so the old bar spread its content
			    across ~830px of dead saturated field. Every class here is `rb-`. */}
			<div className="rangebar">
				<div className="rb-head">
					<span className="rb-cap">Sessions in range</span>
					<span className="rb-total mono">{n(sessionTotals.total)}</span>
					{oldestWaiting > 0 && (
						<span className={`rb-sla ${ageTone(oldestWaiting)}`}>
							oldest in review{' '}
							<b className="mono">{fmtWait(oldestWaiting)}</b>
						</span>
					)}
				</div>

				{/* flexGrow carries a COUNT, not a colour — a proportion is data and
				    changes per render; every colour below comes from a token. */}
				<div
					className="rb-meter"
					role="img"
					aria-label={`${n(sessionTotals.completed)} approved, ${n(
						sessionTotals.processing
					)} under review, ${n(sessionTotals.pending)} incomplete, ${n(
						sessionTotals.failed
					)} rejected, of ${n(sessionTotals.total)} sessions`}
				>
					{parts.segments.map(seg => (
						<span
							key={seg.key}
							className={`rb-seg rb-seg--${seg.key}`}
							style={{ flexGrow: seg.value }}
						/>
					))}
				</div>

				<div className="rb-legs">
					{parts.legs.map(leg => (
						<div
							key={leg.key}
							className={`rb-leg rb-leg--${leg.key}${leg.on ? ' on' : ''}`}
						>
							<div className="rb-n mono">{loaded ? n(leg.value) : '—'}</div>
							<div className="rb-l">
								{leg.label}
								{leg.note && <span className="rb-note mono">{leg.note}</span>}
							</div>
						</div>
					))}
				</div>
			</div>

			<div className="legend">
				node states:
				<span className="nd pass">passed</span>
				{/* same rename as the strip's leg above — the red node state is
				    `rejected` in the renderer (FLOW_STATE), so the legend now names
				    it the way the node itself reports it. */}
				<span className="nd fail">rejected</span>
				<span className="nd review">awaits human review</span>
				<span className="nd active">updated</span>
				<span className="nd unreached">not reached</span>
			</div>

			<div className="cols">
				<div>
					<div className="panel">
						<div className="ph">
							Live sessions
							<span className="r">newest first · {n(rows.length)} shown</span>
						</div>
						<div className="tscroll">
							<table>
								<thead>
									<tr>
										{/* the heading carries the word, so the rows carry only the
											number they are measuring */}
										<th>Applicant · waiting</th>
										<th>Steps in flow</th>
									</tr>
								</thead>
								<tbody>
									{!loaded && (
										<tr>
											<td colSpan={2} className="empty">
												Loading sessions…
											</td>
										</tr>
									)}
									{loaded && !rows.length && (
										<tr>
											<td colSpan={2} className="empty">
												No sessions in this range.
											</td>
										</tr>
									)}
									{rows.map(r => (
										<tr key={r.id}>
											<td className="od odcol">
												{/* THE WAIT, PINNED TO THE CORNER.
												    It is the one thing this column has that Sessions does
												    not — the heading is "Applicant · waiting" — and it is
												    read DOWN the column rather than across a row: how long
												    each session has been sitting is the deck's whole point.
												    In the pill group it started at a different x on every
												    row, because the status and bundle pills before it vary
												    in width, so the times could not be compared at a glance.
												    Anchored top-right they line up in one column. */}
												{r.awaitingReview && (
													<span className={`age corner ${ageTone(r.ageMinutes)}`}>
														{r.age}
													</span>
												)}
												{/* Same order and classes as the Sessions list's Onboarding
												    Details column: fund, applicant, email, phone, the
												    created/updated pair, then the pills. The fund line is
												    suppressed when it merely repeats the applicant's name,
												    exactly as it is there — otherwise the cell opens with
												    the same text twice, once small and once bold. */}
												{fundLabel(r.raw) &&
													fundLabel(r.raw).toLowerCase() !==
														(r.name ?? '').trim().toLowerCase() && (
														<>
															<span className="fund">{fundLabel(r.raw)}</span>
															<br />
														</>
													)}
												<span className="nm">{r.name || '--'}</span>
												<br />
												<span className="mail">{r.email || '--'}</span>
												<br />
												<span className="ph2">
													{r.raw?.contact
														? `${r.raw?.countryCode ?? ''} ${r.raw.contact}`.trim()
														: '--'}
												</span>
												{/* Same line, same place, same class as the sessions list —
													the two Applicant cells are deliberately the same cell. */}
												{r.raw?._id && (
													<>
														<br />
														<span className="sid" title={String(r.raw._id)}>
															<b>id</b> : <span className="sidv">{String(r.raw._id)}</span>
														</span>
													</>
												)}
												<div className="meta">
													<b>Created</b> : {stamp(r.raw?.createdAt)}
													<br />
													<b>Updated</b> : {stamp(r.raw?.updatedAt)}
												</div>
												{/* STATUS FIRST, THEN THE BUNDLE PILL — same order as the
												    sessions list. The status is what the row is scanned for;
												    leading with the fixed "Multi Session Bundle" label put it
												    at a different horizontal position on every bundle row, so
												    the column could not be read down. */}
												<div className="pills">
													<StatusChip verdict={r.verdict}>{r.verdictLabel}</StatusChip>
													{r.raw?.isBundle && (
														<span className="msb">Multi Session Bundle</span>
													)}
												</div>
												{hasOfacWarning(r.raw) && (
													<div className="ofac">
														Business &amp; OFAC sanction alert
													</div>
												)}
											</td>
											<td className="chaincell">
												{/* the same diagram the sessions and onboarding lists draw
													— this deck's own comment always said its chain should
													render identically to Sessions', and Sessions stopped
													being chips when it started drawing the real graph */}
												{/* The diagram and its completion tick share a positioning
												    context: the tick is placed against the RIGHT EDGE OF THE
												    LAST NODE, which is only knowable after react-flow lays
												    the chain out, so FlowDoneTick measures it. */}
												<div className="flowwrap">
													<div className="conditional-flow-steps rowflow">
														<SessionTreeGraph
															details={r.raw}
															isSessionModal
															isSessionList
															/* `stoppedNodeId` is deliberately NOT passed: the chart
															   derives it itself, and its derivation is the better
															   one because it checks the id is actually DRAWN on the
															   canvas — which this deck cannot do without the design.
															   The prop wins over the derivation, so passing a value
															   here would override a good answer with a worse one. */
															runningNodeId={r.runningNodeId}
															stoppedForDays={r.stoppedForDays}
														/>
													</div>
													{/* ANY session that moved in the last 24 hours, not just a
												    finished one. The tick marks the last step that SUCCEEDED,
												    which a part-way session has too — it is the most recent
												    thing that went right, and on a chain still in progress
												    that is exactly what a reviewer scans for. FlowDoneTick
												    hides itself when no step has succeeded yet. */}
												{Date.now() - r.lastActivity < 24 * 60 * 60 * 1000 && (
													<FlowDoneTick
														title={`Last completed step · ${
															r.age || 'in the last 24 hours'
														}`}
													/>
												)}
												</div>
												{/* the lane is locked and scaled to fit, so there has to be
													a way to see the flow at full size — the same modal the
													sessions and onboarding lists open */}
												<div className="fnm">
													<span
														className="vcf"
														onClick={e => {
															e.stopPropagation();
															setFlowView(r.raw);
														}}
													>
														View complete flow
													</span>
													{r.flow ? ` ${r.flow}` : ''}
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
					<div className="foot">
						Every session is in <code>Onboarding → Sessions</code>. A time shows
						only while a compliance agent is being waited on, and it is how long
						THAT wait has run — not how old the session is. Amber is past the{' '}
						{REVIEW_TARGET_MINUTES}-minute review target; red is past two hours.
						It is measured from the session&rsquo;s last update, so anything that
						touches the session mid-wait (a comment, being marked viewed) makes it
						read short. Chains show each step&rsquo;s <i>current</i> state — a
						green pill means the value was verified, not merely submitted.
					</div>
				</div>

				<div>
					<div className="panel">
						<div className="ph">Under review queue</div>
						<table className="kv">
							<tbody>
								{queue.map(q => (
									<tr key={q.label}>
										<td className={q.indent ? 'ind' : ''}>{q.label}</td>
										<td className={q.tone}>{loaded ? n(q.value) : '—'}</td>
									</tr>
								))}
								{oldestWaiting > 0 && (
									<tr>
										<td>Oldest waiting</td>
										<td className="rd">
											{Math.floor(oldestWaiting / 60)}h {oldestWaiting % 60}m
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					<div className="panel">
						<div className="ph">
							Flows on watch
							<span className="r">{watch.length} flagged</span>
						</div>
						{!watch.length && (
							<div className="watch empty">Nothing parked in review.</div>
						)}
						{watch.map(w => (
							<div className="watch" key={w.name}>
								<div className="wt">
									<span className="fn">{w.name}</span>
									<StatusChip verdict="rev">ATTENTION</StatusChip>
									<span className="fm">{n(w.sessions)} sessions</span>
								</div>
								<div className="chain">
									<StepChain steps={w.chain} />
								</div>
								<div className="why">{w.reason}</div>
							</div>
						))}
					</div>

					<div className="panel">
						<div className="ph">Invite email</div>
						<table className="kv">
							<tbody>
								<tr>
									<td>Sent</td>
									<td className="bl">{loaded ? n(email.total) : '—'}</td>
								</tr>
								<tr>
									<td>Opened</td>
									<td className="g">{loaded ? n(email.opened) : '—'}</td>
								</tr>
								<tr>
									<td>Bounced</td>
									<td className={email.bounced ? 'rd' : 'gy'}>
										{loaded ? n(email.bounced) : '—'}
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<div className="panel">
						<div className="ph">
							esign
							<span className="r">
								{envelopes.length ? `${n(envelopes.length)} envelopes` : 'loading…'}
							</span>
						</div>
						<table className="kv">
							<tbody>
								<tr>
									<td>Awaiting signature</td>
									<td className="bl">{n(esign.awaiting)}</td>
								</tr>
								<tr>
									<td>Under review</td>
									<td className="a">{n(esign.review)}</td>
								</tr>
								<tr>
									<td>Completed</td>
									<td className="g">{n(esign.completed)}</td>
								</tr>
								<tr>
									<td>Drafts</td>
									<td className="gy">{n(esign.drafts)}</td>
								</tr>
								<tr>
									<td>Voided</td>
									<td className={esign.voided ? 'rd' : 'gy'}>{n(esign.voided)}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{flowView && (
				<ComplexStepsView
					isOpen={!!flowView}
					closeModal={() => setFlowView(null)}
					details={flowView}
				/>
			)}
		</div>
	);
};

export default OpsDeck;
